// WebSocket signaling client for share-session presence.
//
// Holding this socket open is what makes the recipient visible to the
// patient's device as a live viewer. It carries no session state.
//
// Connects to `wss://{signaling-host}/signal?session={id}` — the real
// ets-signaling route (ring-hash pairing) on the dedicated
// `signaling.blockhaven.health` host (per
// SPEC-INFRA-LINK-SHARE-RECIPIENT-WEB-CONTRACT §5).
//
// Relayed frames are not trusted. ets-signaling forwards the partner
// peer's text and binary frames verbatim and never originates JSON, so
// any frame received here was written by the other peer in the room.
// The client answers a relayed `ping` with a `pong` carrying only the
// session id it already holds, and ignores every other frame, including
// `session_state_changed`, `provider_joined`, `download_progress` and
// `error`. Session state comes only from the `/status` poll in
// share-session.ts.
//
// Reconnect policy (matches hm-ios `WebSocketSignalingService`): a server
// Close is a normal end of the socket (ets-signaling closes a peer the
// room removed, for example when another viewer with the same identity
// connects). The client reconnects after 1, 2, 4, 8 and 16 s, then gives
// up and emits `error`. The attempt counter resets only after a socket
// has stayed open for 60 s, so two sockets displacing each other cannot
// loop.

export type ShareSessionState =
  | "CREATED"
  | "JOINED"
  | "PRESENT"
  | "APPROVED"
  | "DOWNLOADED"
  | "CLOSED"
  | "REVOKED"
  | "EXPIRED";

/**
 * Events raised from local transport state only. No relayed frame
 * produces an event.
 */
export type SignalingEvent =
  | { type: "connected" }
  | { type: "disconnected"; reason: string }
  /** The reconnect budget ran out. Raised only by this client. */
  | { type: "error" };

export const RECONNECT_DELAYS_MS: readonly number[] = [1000, 2000, 4000, 8000, 16000];

/** A socket that stays open this long resets the reconnect budget. */
export const STABLE_CONNECTION_MS = 60_000;

export interface SignalingClientOptions {
  /** Opens one socket. Tests supply a fake. */
  createSocket?: (url: string) => WebSocket;
}

export class SignalingClient {
  /** The one live socket. Callbacks from any other socket are ignored. */
  private ws: WebSocket | null = null;
  /**
   * Bumped by `connect()` and `disconnect()`. Socket callbacks and
   * reconnect timers started under an older generation do nothing.
   */
  private generation = 0;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<(event: SignalingEvent) => void>();
  private readonly createSocket: (url: string) => WebSocket;

  constructor(
    private readonly wsUrl: string,
    /** Bearer token (capability JWS) sent as the `?token=` query parameter. */
    private readonly bearerToken: string,
    options: SignalingClientOptions = {},
  ) {
    this.createSocket = options.createSocket ?? ((url) => new WebSocket(url));
  }

  /** Subscribe to events. Returns an unsubscribe function. */
  on(listener: (event: SignalingEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Start one bounded connection cycle: close any socket this client
   * holds, cancel any pending reconnect, reset the reconnect budget and
   * open a new socket. Used for the first connect and for Try again.
   */
  connect(): void {
    this.generation++;
    this.reconnectAttempts = 0;
    this.teardown();
    this.openSocket(this.generation);
  }

  disconnect(): void {
    this.generation++;
    this.teardown();
  }

  private teardown(): void {
    this.clearReconnectTimer();
    this.clearStableTimer();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try {
        ws.close(1000, "client disconnect");
      } catch {
        // ignore
      }
    }
  }

  private openSocket(gen: number): void {
    // Browsers can't set an Authorization header on a WS upgrade, and
    // ets-signaling reads the capability token only from the handshake:
    // `Authorization: Bearer` or the `?token=` query (bearer_or_query_token,
    // ets-signaling src/main.rs:388). It does not read or select a
    // subprotocol. Present the bearer as `?token=`, the same form as
    // clinician-encounter-web's PresenceSocket.
    let ws: WebSocket;
    try {
      const sep = this.wsUrl.includes("?") ? "&" : "?";
      ws = this.createSocket(
        `${this.wsUrl}${sep}token=${encodeURIComponent(this.bearerToken)}`,
      );
    } catch {
      this.socketEnded("WebSocket construction failed");
      return;
    }
    this.ws = ws;
    const isLive = (): boolean => gen === this.generation && this.ws === ws;

    ws.addEventListener("open", () => {
      if (!isLive()) return;
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        this.stableTimer = null;
        if (isLive()) this.reconnectAttempts = 0;
      }, STABLE_CONNECTION_MS);
      this.emit({ type: "connected" });
      this.sendSubscribe();
    });

    ws.addEventListener("message", (e) => {
      if (!isLive()) return;
      this.handleMessage(typeof e.data === "string" ? e.data : "");
    });

    ws.addEventListener("error", () => {
      // Errors are followed by `close`; don't double-handle.
    });

    ws.addEventListener("close", (e) => {
      if (!isLive()) return;
      this.ws = null;
      this.socketEnded(`code=${e.code}`);
    });
  }

  private handleMessage(text: string): void {
    if (!text) return;
    let type: unknown;
    try {
      type = (JSON.parse(text) as { type?: unknown } | null)?.type;
    } catch {
      return;
    }
    if (type === "ping") this.sendPong();
    // Every other relayed frame is ignored (see the header comment).
  }

  /**
   * The live socket ended: a server Close, a network failure or a failed
   * construction. Reconnect with backoff, or give up once the budget is
   * spent.
   */
  private socketEnded(reason: string): void {
    this.clearStableTimer();
    this.emit({ type: "disconnected", reason });

    if (this.reconnectAttempts >= RECONNECT_DELAYS_MS.length) {
      this.emit({ type: "error" });
      return;
    }
    const delay = RECONNECT_DELAYS_MS[this.reconnectAttempts]!;
    this.reconnectAttempts++;
    const gen = this.generation;
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (gen === this.generation) this.openSocket(gen);
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  private sendSubscribe(): void {
    this.send({
      type: "subscribe",
      session_id: this.sessionIdFromUrl(),
      timestamp: new Date().toISOString(),
    });
  }

  private sendPong(): void {
    this.send({
      type: "pong",
      session_id: this.sessionIdFromUrl(),
      timestamp: new Date().toISOString(),
    });
  }

  private send(envelope: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(envelope));
    }
  }

  private sessionIdFromUrl(): string {
    // Pull session id out of `wss://host/signal?session={id}`.
    const m = this.wsUrl.match(/[?&]session=([^&]+)/);
    return m?.[1] ? decodeURIComponent(m[1]) : "";
  }

  private emit(event: SignalingEvent): void {
    for (const l of this.listeners) {
      try {
        l(event);
      } catch (e) {
        console.warn("signaling listener threw:", e);
      }
    }
  }
}
